import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Separator } from 'components/ui/separator';
import { Switch } from 'components/ui/switch';
import { Badge } from 'components/ui/badge';
import { FiSave, FiWifi, FiActivity } from 'react-icons/fi';
import { ElectronPrintingApi } from 'services/printing/renderer/ElectronPrintingApi';
import { IPPConfig } from 'services/printing/types';

const STORAGE_KEY = 'printing.ippConfig';

const PrintingSettings: React.FC = () => {
  const initialConfig = useMemo(
    () => ElectronPrintingApi.createDefaultConfig(),
    [],
  );
  const [config, setConfig] = useState<IPPConfig>(initialConfig);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<null | 'ok' | 'err'>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<null | 'ok' | 'err'>(null);
  const [electronAvailable, setElectronAvailable] = useState(false);

  useEffect(() => {
    ElectronPrintingApi.isAvailable()
      .then(setElectronAvailable)
      .catch(() => setElectronAvailable(false));
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setConfig(JSON.parse(raw));
    } catch {
      // ignore corrupt storage
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(null);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      setSaved('ok');
    } catch (e) {
      console.error('Failed to save printer settings:', e);
      setSaved('err');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const report = await ElectronPrintingApi.connect(config);
      setTestResult(report.connected ? 'ok' : 'err');
      if (!report.connected) {
        console.warn('Printer test diagnostics:', report.diagnostics);
      }
    } catch (e) {
      console.error('Printer test failed:', e);
      setTestResult('err');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Paramètres IPP (Ricoh)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheme">Protocole</Label>
              <select
                id="scheme"
                className="border rounded h-9 px-2 text-sm bg-background"
                value={config.scheme || 'ipp'}
                onChange={e =>
                  setConfig(c => ({
                    ...c,
                    scheme: e.target.value as 'ipp' | 'ipps',
                  }))
                }
              >
                <option value="ipp">ipp</option>
                <option value="ipps">ipps</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="host">Adresse IP / Hôte</Label>
              <Input
                id="host"
                value={config.host}
                onChange={e => setConfig(c => ({ ...c, host: e.target.value }))}
                placeholder="ex: 192.168.1.50"
                spellCheck={false}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="port">Port</Label>
              <Input
                id="port"
                type="number"
                value={config.port}
                onChange={e =>
                  setConfig(c => ({ ...c, port: Number(e.target.value || 0) }))
                }
                placeholder="631"
                min={1}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resource">Chemin IPP</Label>
              <Input
                id="resource"
                value={config.resource || '/ipp/print'}
                onChange={e =>
                  setConfig(c => ({
                    ...c,
                    resource: e.target.value || '/ipp/print',
                  }))
                }
                placeholder="/ipp/print (ou /ipp/printer)"
                spellCheck={false}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeout">Timeout (ms)</Label>
              <Input
                id="timeout"
                type="number"
                min={1000}
                step={500}
                value={config.timeoutMs ?? 15000}
                onChange={e =>
                  setConfig(c => ({
                    ...c,
                    timeoutMs: Number(e.target.value || 0),
                  }))
                }
                placeholder="15000"
              />
            </div>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-sm font-medium">Capacités du finisher</p>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config.capabilities.supportsA3}
                  onCheckedChange={checked =>
                    setConfig(c => ({
                      ...c,
                      capabilities: {
                        ...c.capabilities,
                        supportsA3: !!checked,
                      },
                    }))
                  }
                />
                <Label>Support A3</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config.capabilities.supportsFolding}
                  onCheckedChange={checked =>
                    setConfig(c => ({
                      ...c,
                      capabilities: {
                        ...c.capabilities,
                        supportsFolding: !!checked,
                      },
                    }))
                  }
                />
                <Label>Pliage</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config.capabilities.supportsStapling}
                  onCheckedChange={checked =>
                    setConfig(c => ({
                      ...c,
                      capabilities: {
                        ...c.capabilities,
                        supportsStapling: !!checked,
                      },
                    }))
                  }
                />
                <Label>Agrafage</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={config.capabilities.supportsOffsetStacking}
                  onCheckedChange={checked =>
                    setConfig(c => ({
                      ...c,
                      capabilities: {
                        ...c.capabilities,
                        supportsOffsetStacking: !!checked,
                      },
                    }))
                  }
                />
                <Label>Décalage des feuilles</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="finisher">Modèle de finisher (optionnel)</Label>
              <Input
                id="finisher"
                value={config.capabilities.finisher ?? ''}
                onChange={e =>
                  setConfig(c => ({
                    ...c,
                    capabilities: {
                      ...c.capabilities,
                      finisher: e.target.value || undefined,
                    },
                  }))
                }
                placeholder="ex: DF-3030"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Utilisé à titre indicatif; les capacités effectives sont
                validées côté appareil.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6">
            <Button onClick={handleSave} disabled={saving}>
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button
              onClick={handleTest}
              variant="outline"
              disabled={!electronAvailable || testing}
            >
              <FiWifi className="mr-2 h-4 w-4" />
              {testing ? 'Test…' : 'Tester la connexion'}
            </Button>

            {saved === 'ok' && (
              <Badge className="ml-1" variant="outline">
                Paramètres enregistrés
              </Badge>
            )}
            {saved === 'err' && (
              <Badge className="ml-1" variant="destructive">
                Échec enregistrement
              </Badge>
            )}
            {testResult === 'ok' && (
              <Badge className="ml-1" variant="outline">
                <FiActivity className="mr-1" /> Connexion OK
              </Badge>
            )}
            {testResult === 'err' && (
              <Badge className="ml-1" variant="destructive">
                Connexion échouée
              </Badge>
            )}
          </div>

          {!electronAvailable && (
            <p className="mt-3 text-xs text-muted-foreground">
              L’API Electron n’est pas disponible (mode web) — les tests IPP
              sont désactivés.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrintingSettings;
